import React, { useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useBook } from '../../contexts/BookContext';
import { toast } from 'react-toastify';

const steps = [
  { name: 'Story', path: '/edit', step: 1 },
  { name: 'World', path: '/world', step: 2 },
  { name: 'Design', path: '/design', step: 3 },
  { name: 'Print', path: '/print', step: 4 },
  { name: 'Ship', path: '/ship', step: 5 },
];

export default function ProgressSteps() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentBook, hasActualSpreads, isDesignReadyForViewing,allowedProgress, hasActiveImageGeneration } = useBook();
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showImageGenWarningModal, setShowImageGenWarningModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Determine current step based on path
  const currentStep = steps.findIndex(step => 
    location.pathname.startsWith(step.path) || 
    (step.path === '/edit' && location.pathname.startsWith('/create'))
  ) + 1;

  // Allow skipping to Design (step 3) if we have a book with actual spreads
  // Allow skipping to Print/Ship (step 4+) only if preview processing is complete
  const canVisitDesign = !!currentBook && hasActualSpreads;
  const canSkipForward = !!currentBook && hasActualSpreads && isDesignReadyForViewing;
  // World step availability: only when world data exists
  const canVisitWorld = !!currentBook && hasActualSpreads;

  const isDesignRoute = useMemo(() => location.pathname.startsWith('/design'), [location.pathname]);

  const navigateToStep = useCallback((step, path) => {
    if (step === 1 && currentBook) {
      navigate(`/edit/${currentBook.id}`);
      return;
    }

    if (step > 1 && currentBook) {
      navigate(`${path}/${currentBook.id}`);
    } else {
      navigate(path);
    }
  }, [currentBook, navigate]);

  const findSpreadWithPendingImages = useCallback(() => {
    if (!currentBook?.spreads) return null;
    
    for (const [spreadId, spread] of Object.entries(currentBook.spreads)) {
      const elements = spread?.elements || [];
      const hasPendingImage = elements.some(el =>
        el.type === 'image' &&
        (el.imageGenerationStatus === 'pending' || !!el.imageUpdateJobId)
      );
      if (hasPendingImage) {
        return spreadId;
      }
    }
    return null;
  }, [currentBook?.spreads]);

  const handleWaitForImages = useCallback(() => {
    const spreadWithPendingImages = findSpreadWithPendingImages();
    console.log('[ProgressSteps] Wait for images clicked:', { spreadWithPendingImages, bookId: currentBook?.id });
    if (spreadWithPendingImages && currentBook?.id) {
      const spreadNumber = spreadWithPendingImages === 'spread-0' ? 0 : parseInt(spreadWithPendingImages.split('-')[1], 10);
      const navPath = `/design/${currentBook.id}/${spreadNumber}`;
      console.log('[ProgressSteps] Navigating to:', navPath);
      navigate(navPath);
    } else {
      console.log('[ProgressSteps] No spread with pending images found');
    }
    setShowImageGenWarningModal(false);
    setPendingNavigation(null);
  }, [findSpreadWithPendingImages, currentBook?.id, navigate]);

  const handleStepClick = (step, path) => {
    // Check if we're trying to access Print or Ship steps when processing isn't complete
    const isPrintOrShip = path === '/print' || path === '/ship';
    if (isPrintOrShip && currentBook && allowedProgress < 3) {
      // Show error message or toast notification
      toast.warning('Book generation is still in progress. Please wait for it to complete before proceeding to Print or Ship.');
      return;
    }
    
    // Allow clicking future steps based on their requirements
    // Design is allowed if spreads exist; World if world data exists; Print/Ship if processing complete
    if (step > currentStep && !canSkipForward) {
      if (!(path === '/design' && canVisitDesign) && !(path === '/world' && canVisitWorld)) return;
    }

    // Warn if navigating to Print or Ship while images are still generating
    if ((path === '/print' || path === '/ship') && hasActiveImageGeneration) {
      setPendingNavigation(() => () => navigateToStep(step, path));
      setShowImageGenWarningModal(true);
      return;
    }
    
    const isLeavingDesign = isDesignRoute && path !== '/design';
    const hasUnsavedChanges = typeof window !== 'undefined' && !!window.__sbwDesignIsDirty;

    if (isLeavingDesign && hasUnsavedChanges) {
      setPendingNavigation(() => () => navigateToStep(step, path));
      setShowUnsavedChangesModal(true);
      return;
    }

    navigateToStep(step, path);
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4">
      <div className="relative flex items-center justify-between">
        {/* Progress line */}
        <div 
          className="absolute left-0 right-0 top-5 -translate-y-1/2" 
          aria-hidden="true"
        >
          <div className="h-0.5 w-full bg-gray-200" />
          <div 
            className="h-0.5 bg-indigo-600 transition-all duration-500 ease-in-out absolute top-0 left-0"
            style={{ 
              width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`
            }}
          />
        </div>

        {/* Steps */}
        {steps.map(({ name, step, path }) => {
          const isActive = step === currentStep;
          const isComplete = step < currentStep;
          const isProcessingIncomplete = (path === '/print' || path === '/ship') && (allowedProgress <= 2);
          // Design is allowed if spreads exist; World if world data exists; Print/Ship if processing complete
          const isFuture = ((step > currentStep && (!(path === '/design' && canVisitDesign) && !canSkipForward && !(path === '/world' && canVisitWorld))) || isProcessingIncomplete);

          return (
            <div 
              key={name} 
              className="flex flex-col items-center relative"
            >
              <button
                onClick={() => handleStepClick(step, path)}
                disabled={isFuture}
                className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
                  {
                    'bg-indigo-600 text-white': isActive,
                    'bg-indigo-600 text-white hover:bg-indigo-700': isComplete,
                    'bg-gray-200 cursor-not-allowed': isFuture,
                    'hover:bg-indigo-500': !isActive && !isComplete && !isFuture,
                    'bg-white border-2 border-indigo-600 hover:bg-indigo-50': !isActive && !isComplete && canSkipForward && !isProcessingIncomplete,
                  }
                )}
              >
                <span className={clsx(
                  'text-sm font-semibold',
                  {
                    'text-white': isActive || isComplete,
                    'text-gray-500': isFuture,
                  }
                )}>
                  {step}
                </span>
              </button>
              <span 
                className={clsx(
                  'mt-2 text-sm font-medium',
                  {
                    'text-indigo-600': isActive,
                    'text-gray-900': isComplete,
                    'text-gray-500': isFuture,
                  }
                )}
              >
                {name}
              </span>
            </div>
          );
        })}
      </div>

      {showImageGenWarningModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setShowImageGenWarningModal(false);
                setPendingNavigation(null);
              }}
            />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Images Still Generating
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      One or more images are still being generated. Proceeding now may result in missing images in your final book.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6 space-y-3">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={handleWaitForImages}
                >
                  Wait for Images
                </button>
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => {
                    const fn = pendingNavigation;
                    setShowImageGenWarningModal(false);
                    setPendingNavigation(null);
                    fn?.();
                  }}
                >
                  Continue Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUnsavedChangesModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setShowUnsavedChangesModal(false);
                setPendingNavigation(null);
              }}
            />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Unsaved changes
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You have unsaved changes. If you leave now, your changes may be lost.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6 space-y-3">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => {
                    (async () => {
                      const fn = pendingNavigation;
                      const saved = await window.__sbwDesignSave?.();
                      setShowUnsavedChangesModal(false);
                      setPendingNavigation(null);
                      if (saved !== false) {
                        fn?.();
                      }
                    })();
                  }}
                >
                  Save and Continue
                </button>
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
                  onClick={() => {
                    const fn = pendingNavigation;
                    setShowUnsavedChangesModal(false);
                    setPendingNavigation(null);
                    fn?.();
                  }}
                >
                  Continue without Saving Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}